/** Account scriptId that should currently own `window.bc`. */
let requestedScriptId: string | null = null;

/** scriptId whose bundle last successfully claimed ownership for `requestedScriptId`. */
let activeScriptId: string | null = null;

/** In-flight Brightcove script loads keyed by scriptId (shared by concurrent callers). */
const pendingBrightcodeLoadByScriptId = new Map<string, Promise<void>>();

/** Last URL seen per scriptId — needed to reclaim after a stale load. */
const scriptUrlById = new Map<string, string>();

/**
 * Brightcove player bundles inject global player CSS into `<head>` with
 * `div { max-width: 2220px !important }`, which breaks site layout. The rule
 * lives on `style.bc-style-{player}-{embed}` (and is skipped when that node
 * already exists), so strip it whenever a bundle finishes loading.
 */
const BRIGHTCOVE_GLOBAL_DIV_MAX_WIDTH_RULE =
  /div\s*\{\s*max-width\s*:\s*2220px\s*!important\s*;?\s*\}/gi;

const stripBrightcoveGlobalDivMaxWidth = () => {
  document
    .querySelectorAll('style[class^="bc-style-"], style#bc-style-vjs')
    .forEach(styleEl => {
      const css = styleEl.textContent;
      if (!css.includes('2220px')) {
        return;
      }

      const sanitized = css.replace(BRIGHTCOVE_GLOBAL_DIV_MAX_WIDTH_RULE, '');
      if (sanitized !== css) {
        styleEl.textContent = sanitized;
      }
    });
};

/** Clears active-account tracking — for unit tests only. */
export const resetBrightcoveScriptCacheForTests = () => {
  requestedScriptId = null;
  activeScriptId = null;
  pendingBrightcodeLoadByScriptId.clear();
  scriptUrlById.clear();
};

/**
 * Loads a Brightcove player `index.min.js` for `scriptId`
 * (`accountId`-`playerId`-`embed`).
 *
 * Brightcove's global `bc` is overwritten by each account's bundle, and the
 * registry key is `playerId_embed` (typically `default_default`) — not the
 * account. After SPA navigation from account A → B, `window.bc` belongs to B.
 * Returning to A must re-execute A's script so `bc()` uses A's policy key.
 *
 * A fast A→B→A can leave B's script in flight. B must not claim ownership (or
 * leave `window.bc` on B) after A has been requested again — stale completions
 * reclaim the requested account.
 *
 * Same-account callers share one in-flight load, and once that account owns
 * `window.bc`, further calls resolve immediately.
 *
 * Failed loads are not remembered; the script tag is removed so a later
 * attempt can retry instead of hanging on a never-firing load event.
 */
export const loadBrightcoveScript = (
  scriptUrl: string,
  scriptId: string,
): Promise<void> => {
  scriptUrlById.set(scriptId, scriptUrl);
  requestedScriptId = scriptId;

  // Another account was active — drop ownership until this load claims it.
  // Without this, A→B→A while B is in flight early-returns for A and then B's
  // completion overwrites window.bc under the wrong account.
  if (activeScriptId !== null && activeScriptId !== scriptId) {
    activeScriptId = null;
  }

  if (activeScriptId === scriptId) {
    return Promise.resolve();
  }

  const pending = pendingBrightcodeLoadByScriptId.get(scriptId);
  if (pending) {
    return pending;
  }

  // Re-inject so the bundle re-runs and reassigns window.bc to this account.
  document.getElementById(scriptId)?.remove();

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = scriptUrl;
    script.async = true;

    script.addEventListener(
      'load',
      () => {
        stripBrightcoveGlobalDivMaxWidth();
        pendingBrightcodeLoadByScriptId.delete(scriptId);

        if (requestedScriptId === scriptId) {
          activeScriptId = scriptId;
          resolve();
          return;
        }

        // Stale: this bundle already assigned window.bc, but a newer account
        // was requested. Reclaim so bc matches requestedScriptId.
        resolve();
        const wantedId = requestedScriptId;
        const wantedUrl = wantedId ? scriptUrlById.get(wantedId) : undefined;
        if (wantedId && wantedUrl) {
          activeScriptId = null;
          void loadBrightcoveScript(wantedUrl, wantedId);
        }
      },
      { once: true },
    );
    script.addEventListener(
      'error',
      () => {
        pendingBrightcodeLoadByScriptId.delete(scriptId);
        script.remove();
        reject(new Error(`Failed to load Brightcove script ${scriptUrl}`));
      },
      { once: true },
    );

    document.body.appendChild(script);
  });

  pendingBrightcodeLoadByScriptId.set(scriptId, promise);
  return promise;
};
